// Get item SKUs in cart that match any SKUs in restrictedItems
import { CartFragment } from '../../graphql/magento'
import { cartItemPrice } from '../../utils/priceTools'
import { Maybe } from '../../utils/types'
import { ShippingCountry } from './hooks/useShippingCountries'
import { RestrictedItem } from './types'

export type SanityRestrictedSkusFilters =
  | {
      country: string
      type?: 'region'
      region: string
    }
  | {
      country: string
      type: 'all-regions'
    }

export const filterSanityRestrictedSkus =
  (shippingCountries: ShippingCountry[]) =>
  (filters: SanityRestrictedSkusFilters): Maybe<string>[] =>
    shippingCountries
      .find((shippingCountry) => shippingCountry.country === filters.country)
      ?.shippingRegions?.find((shippingRegion) =>
        filters.type === 'all-regions'
          ? shippingRegion?.isProductsRestrictedForAllRegions
          : shippingRegion?.region?.split('-')[1] === filters.region,
      )
      ?.restrictedProducts?.map((product) => product?.sku) ?? []

/**
 * Cart line items whose SKU satisfies `matches`, in the shared `RestrictedItem` shape. Shared by
 * the shipping-restriction and Spotlight-kit extractors so the mapping lives in one place.
 */
export const extractCartItemsMatching = ({
  customerCart,
  matches,
}: {
  customerCart: CartFragment
  matches: (sku: Maybe<string>) => boolean
}): RestrictedItem[] =>
  (customerCart.items || []).reduce((acc: RestrictedItem[], item) => {
    const { id, product, quantity } = item || {}
    const { name, sku } = product || {}

    if (id && matches(sku)) {
      acc.push({
        id,
        name: name || sku || id,
        price: cartItemPrice({ item }),
        quantity: quantity ?? 0,
        sku: sku || '',
      })
    }
    return acc
  }, [])

export const extractCartRestrictedItems = ({
  customerCart,
  restrictedSkus,
}: {
  customerCart: CartFragment
  restrictedSkus: Maybe<string>[]
}): RestrictedItem[] =>
  extractCartItemsMatching({
    customerCart,
    matches: (sku) => !!restrictedSkus?.includes(sku),
  })

export type CartShippingRestriction =
  | { _tag: 'country-banned' }
  | { _tag: 'all-regions'; restrictedItems: RestrictedItem[] }
  | { _tag: 'region'; region: string; restrictedItems: RestrictedItem[] }

// Country options the shipping form presents as countries but Magento persists on the cart
// address as country + region
const specialCountryOptionFilters: Record<string, { country: string; region: string }> = {
  HK: { country: 'CN', region: 'HK' },
  GU: { country: 'US', region: 'GU' },
  VI: { country: 'US', region: 'VI' },
}

/**
 * Determine whether shipping the current cart to the given address is restricted, in order
 * of severity: the country is not shipped to at all, items in the cart are restricted for
 * every region of the country, or items in the cart are restricted for the address region.
 *
 * `country`/`region` may be a candidate address from the shipping form (where Hong Kong,
 * Guam, and Virgin Islands appear as countries and are mapped here) or the address
 * persisted on the cart (already stored as CN/HK, US/GU, US/VI).
 */
export const getAddressShippingRestriction = ({
  customerCart,
  shippingCountries,
  country,
  region,
}: {
  customerCart: CartFragment
  shippingCountries: ShippingCountry[]
  country: string
  region?: string
}): CartShippingRestriction | undefined => {
  const mappedCountryOption = specialCountryOptionFilters[country]

  const isCountryBanned = (countryCode: string): boolean =>
    shippingCountries.some(
      (shippingCountry) =>
        shippingCountry.country === countryCode && !!shippingCountry.isShippingRestricted,
    )
  if (
    (mappedCountryOption && isCountryBanned(mappedCountryOption.country)) ||
    isCountryBanned(country)
  ) {
    return { _tag: 'country-banned' }
  }

  const restrictedSkusFor = filterSanityRestrictedSkus(shippingCountries)
  const restrictedForAllRegions = extractCartRestrictedItems({
    customerCart,
    restrictedSkus: restrictedSkusFor({ country, type: 'all-regions' }),
  })
  if (restrictedForAllRegions.length) {
    return { _tag: 'all-regions', restrictedItems: restrictedForAllRegions }
  }

  const regionFilters = [
    ...(mappedCountryOption ? [mappedCountryOption] : []),
    ...(region ? [{ country, region }] : []),
  ]
  const restrictedForRegion = regionFilters
    .map((filters) => ({
      region: filters.region,
      restrictedItems: extractCartRestrictedItems({
        customerCart,
        restrictedSkus: restrictedSkusFor(filters),
      }),
    }))
    .find(({ restrictedItems }) => restrictedItems.length > 0)

  return restrictedForRegion ? { _tag: 'region', ...restrictedForRegion } : undefined
}

/**
 * Determine whether any items in the cart are restricted for the cart's persisted shipping
 * address. Evaluates only durable cart state (items + shipping address saved on the cart),
 * so the result holds at place-order time regardless of page refreshes or other resets of
 * in-memory checkout state.
 */
export const getCartShippingRestriction = ({
  customerCart,
  shippingCountries,
}: {
  customerCart: CartFragment
  shippingCountries: ShippingCountry[]
}): CartShippingRestriction | undefined => {
  const cartAddress = customerCart.shipping_addresses?.[0]
  const country = cartAddress?.country?.code
  if (!country) {
    return undefined
  }

  return getAddressShippingRestriction({
    customerCart,
    shippingCountries,
    country,
    region: cartAddress?.region?.code ?? undefined,
  })
}
